import React, { useEffect, useState, Suspense, lazy } from "react"
import { createPortal } from "react-dom"
import { useRoute } from "react-router5"
import { connect } from "react-redux"
import { PageLoader } from "./pages/Contracts/ContractList/PageLoader"
import {mobileMaxWidthMediaQuery} from "./methods/utils"
import { setGlobalAppContext } from './contexts/AppContext'

import classnames from "classnames"

import "styles/main.sass"
import "styles/MUI-style.sass"
import "styles/components/popup.sass"
import "pages/Styles/UIKit.sass"
import "pages/Styles/EditContractCommon.sass"
import "pages/Styles/TemplatesCommon.sass"
import "pages/Styles/ContractPageBranding.sass"
import "pages/EditContract/OnboardingEditorTour.sass"

import Icon from "./components/Icon"
import Nav from "./components/elements/Nav"
import MobileFooter from "./components/elements/MobileFooter"

import imgLogoFull from "images/logo-full.svg"
import imgLogoFullHighlighted from "images/logo-full-highlighted.svg"

import {Badge, Fab, useMediaQuery, Menu, MenuItem, ListItemIcon, ListItemText, Divider} from "@mui/material"
import isAuthorized from "utils/funcs/isAuthorized"
import BalanceHolder from "components/elements/nav/BalanceHolder"
import {ReactComponent as Close} from "./pages/Icons/size24/Close.svg";
import {ReactComponent as PDFViewer} from "./components/PDFViewer.svg";
import {ReactComponent as SignatureCanvas} from "./components/SignatureCanvas.svg";
import {LocalizationProvider, ruRU} from '@mui/x-date-pickers';
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import SupportHelpCenter from "./components/SupportHelpCenter"
import { ReactComponent as MaxIcon } from "images/pages/iconsV2/max.svg"
import {
  Phone as PhoneIcon,
  Telegram as TelegramIcon,
  WhatsApp as WhatsAppIcon,
  Person,
  Chat as ChatIcon,
  LiveHelpRounded as LiveHelpChatIcon,
} from "@mui/icons-material"
import {openInNewTab, supportLink, maxSupportLink} from "./methods/openInNewTab";
import { captureUtmFirstTouch } from "./methods/utmFirstTouch";
import {
  UI_LAYER_SUPPORT_FAB as SUPPORT_Z_FAB,
  UI_LAYER_SUPPORT_FAB_BADGE as SUPPORT_Z_FAB_UNREAD_BADGE,
  UI_LAYER_GLOBAL_NOTIFICATION,
  UI_LAYER_SUPPORT_MENU as SUPPORT_Z_MENU,
} from "./constants/uiLayers"
import { EXPERIMENT_TEMPLATE_SUPPORT_AI_CHAT } from "./constants/userExperiments"
import cleanPhoneNumber from "./methods/cleanPhoneNumber";
import {getToken, saveToken, isOAuth2User, loadToken} from "./methods/saveToken";
import {loadSelf} from "./methods/loadSelf";
import {trackGoal} from "./methods/trackGoal";
import StackTrace from "stacktrace-js";
import { trackPerformance } from './methods/performance';
import errorLogger from './utils/errorLogger';
import { useApplyContractPageBranding } from './hooks/useApplyContractPageBranding';
import { ContractPageBrandingBar } from './components/ContractPageBrandingBar'
import {
  hasActiveBranding,
  hasBrandingHeaderContent,
  MOBILE_BRANDING_BAR_HEIGHT_PX,
} from './utils/contractPageBranding';
import TariffsV2Page from "pages/Tariffs/TariffsV2Page"
import { ProfileTabs } from "./pages/Profile/Profile"
import SigningAssistantChat from "./chat-kit/SigningAssistantChat"

// Расширяем типы для window
declare global {
    interface Window {
        __webpack_require__?: {
            e: (chunkId: string | number) => Promise<any>;
            c: { [key: string]: any };
        };
        webpackHotUpdate?: any;
        gtag?: (...args: any[]) => void;
    }
}

// Lazy load all pages
const AuthForm = lazy(() => import("./pages/Auth/AuthForm"))
const AnotherPage = lazy(() => import("./pages/Another"))
const RegisterPage = lazy(() => import("./pages/Auth/Register"))
const NewContractPage = lazy(() => import("./pages/EditContract/NewContract"))
const ProfilePage = lazy(() => import("./pages/Profile/Profile"))
const CreatePDFPage = lazy(() => import("./pages/EditContract/CreatePDF/CreatePDFPage"))
const ContractViewPage = lazy(() => import("./pages/Contracts/ContractView/ContractViewPage"))
const ContractListPage = lazy(() => import("./pages/Contracts/ContractList/ContractListPage"))
const ContractStatisticsPage = lazy(() => import("./pages/Contracts/Statistics/ContractStatisticsPage"))
const WelcomePopup = lazy(() => import("./pages/Profile/WelcomePopup"))
const UserStoryPopup = lazy(() => import("./pages/Profile/UserStoryPopup"))
const OnboardingPayOfferPopup = lazy(() => import("./components/OnboardingPayOfferPopup"))
const SharedTemplatePage = lazy(() => import("./pages/ShareTemplate/SharedTemplatePage"))
const LegalCheckPage = lazy(() => import("./pages/LegalCheck/LegalCheckPage"))
const LegalHelpPage = lazy(() => import("./pages/LegalHelp/LegalHelpPage"))
const ClosingDocumentsPage = lazy(() => import("./pages/Contracts/ClosingDocumentsPage"))
const PartnerProgramPage = lazy(() => import("./pages/PartnerProgram/PartnerProgramPage"))
const ContractBrandingPage = lazy(() => import("./pages/ContractBranding/ContractBrandingPage"))
const DebugPage = lazy(() => import("./pages/Debug/DebugPage"))


let notificationTimeout: any = null

const MAX_RETRY_ATTEMPTS = 10;
const INITIAL_RETRY_DELAY = 1000; // 1 секунда для первых 5 попыток
const EXPONENTIAL_RETRY_DELAY = 1000; // Базовый интервал для экспоненциального роста
const MAX_DELAY = 20000; // Максимальная задержка 20 секунд
const FIXED_RETRIES = 5; // Количество попыток с фиксированным интервалом

const retryLoadChunk = async (chunkId: string, attempts = 0): Promise<any> => {
  try {
    // Извлекаем имя чанка из URL
    const chunkName = chunkId.split('/').pop()?.split('.')[0];
    if (!chunkName) {
      console.error('Не удалось определить имя чанка из URL:', chunkId);
      throw new Error('Не удалось определить имя чанка');
    }
    
    console.log(`[ChunkLoader] Попытка загрузки чанка:`, {
      chunkId,
      chunkName,
      attempt: attempts + 1,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent
    });

    // Пытаемся загрузить чанк по имени
    const chunk = await import(/* webpackChunkName: "[request]" */ `./pages/${chunkName}`);
    
    console.log(`[ChunkLoader] Чанк успешно загружен:`, {
      chunkId,
      chunkName,
      attempt: attempts + 1,
      timestamp: new Date().toISOString()
    });
    
    return chunk;
  } catch (error: any) {
    console.error(`[ChunkLoader] Ошибка загрузки чанка:`, {
      chunkId,
      chunkName: chunkId.split('/').pop()?.split('.')[0],
      error: error.message,
      stack: error.stack,
      attempt: attempts + 1,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent
    });

    if (attempts < MAX_RETRY_ATTEMPTS) {
      let delay: number;
      if (attempts < FIXED_RETRIES) {
        delay = INITIAL_RETRY_DELAY;
      } else {
        const exponentialDelay = EXPONENTIAL_RETRY_DELAY * Math.pow(2, attempts - FIXED_RETRIES);
        delay = Math.min(exponentialDelay, MAX_DELAY);
      }
      
      console.log(`[ChunkLoader] Повторная попытка через ${delay}ms:`, {
        chunkId,
        attempt: attempts + 1,
        nextAttempt: attempts + 2,
        delay,
        timestamp: new Date().toISOString()
      });
      
      // Агрессивная очистка кэша при повторных попытках
      await clearAllCaches();
      
      await new Promise(resolve => setTimeout(resolve, delay));
      return retryLoadChunk(chunkId, attempts + 1);
    }
    
    console.error('[ChunkLoader] Исчерпаны все попытки загрузки чанка:', {
      chunkId,
      chunkName: chunkId.split('/').pop()?.split('.')[0],
      attempts,
      error: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      performance: {
        resourceCount: performance.getEntriesByType('resource').length,
        totalResourceDuration: performance.getEntriesByType('resource')
          .reduce((total: number, entry: any) => total + entry.duration, 0),
        navigationTiming: performance.timing
      }
    });
    
    throw error;
  }
};

// Функция для агрессивной очистки всех кэшей
const clearAllCaches = async (): Promise<void> => {
  try {
    console.log('[CacheClear] Начинаем агрессивную очистку кэша...');
    
    // 1. Очищаем Cache API
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      console.log(`[CacheClear] Найдено ${cacheNames.length} кэшей для очистки`);
      
      for (const cacheName of cacheNames) {
        console.log(`[CacheClear] Удаляем кэш: ${cacheName}`);
        await caches.delete(cacheName);
      }
    }
    
    // 2. Очищаем Service Worker кэши
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      console.log('[CacheClear] Отправляем сообщение Service Worker для очистки кэша');
      navigator.serviceWorker.controller.postMessage({
        type: 'CLEAR_ALL_CACHES'
      });
    }
    
    // 3. Очищаем localStorage и sessionStorage (кроме критических данных)
    // token — OAuth2-флаг сессии (access в памяти); access_token — legacy
    const criticalKeys = ['registerForm', 'redirectAfterAuth', 'access_token', 'token', 'session_data'];
    const localStorageKeys = Object.keys(localStorage);
    const sessionStorageKeys = Object.keys(sessionStorage);
    
    console.log(`[CacheClear] Очищаем localStorage (${localStorageKeys.length} ключей)`);
    localStorageKeys.forEach(key => {
      if (!criticalKeys.includes(key)) {
        localStorage.removeItem(key);
        console.log(`[CacheClear] Удален ключ localStorage: ${key}`);
      } else {
        console.log(`[CacheClear] Сохранен критический ключ localStorage: ${key}`);
      }
    });
    
    console.log(`[CacheClear] Очищаем sessionStorage (${sessionStorageKeys.length} ключей)`);
    sessionStorageKeys.forEach(key => {
      if (!criticalKeys.includes(key)) {
        sessionStorage.removeItem(key);
        console.log(`[CacheClear] Удален ключ sessionStorage: ${key}`);
      } else {
        console.log(`[CacheClear] Сохранен критический ключ sessionStorage: ${key}`);
      }
    });
    
    // 4. Очищаем IndexedDB (если есть)
    if ('indexedDB' in window) {
      try {
        const databases = await indexedDB.databases();
        for (const db of databases) {
          if (db.name && !db.name.includes('critical')) {
            console.log(`[CacheClear] Удаляем IndexedDB: ${db.name}`);
            indexedDB.deleteDatabase(db.name);
          }
        }
      } catch (e) {
        console.log('[CacheClear] Не удалось очистить IndexedDB:', e);
      }
    }
    
    // 5. Принудительно очищаем кэш webpack
    if (typeof window.__webpack_require__ !== 'undefined' && window.__webpack_require__?.c) {
      console.log('[CacheClear] Очищаем кэш webpack modules');
      const webpackCache = window.__webpack_require__?.c;
      if (webpackCache) {
        Object.keys(webpackCache).forEach(key => {
          delete webpackCache[key];
        });
      }
    }
    
    console.log('[CacheClear] Агрессивная очистка кэша завершена');
  } catch (error) {
    console.error('[CacheClear] Ошибка при очистке кэша:', error);
  }
};

// Функция для принудительной перезагрузки с очисткой кэша
const forceReloadWithCacheClear = (): void => {
  console.log('[ForceReload] Принудительная перезагрузка с очисткой кэша');
  
  // Добавляем параметры для принудительной перезагрузки
  const timestamp = Date.now();
  const newUrl = new URL(window.location.href);
  newUrl.searchParams.set('v', '0.1.1');
  newUrl.searchParams.set('t', timestamp.toString());
  newUrl.searchParams.set('cache_clear', 'true');
  
  // Очищаем кэш перед перезагрузкой
  clearAllCaches().then(() => {
    console.log('[ForceReload] Перезагружаем страницу:', newUrl.toString());
    
    // Используем location.replace для полной перезагрузки
    window.location.replace(newUrl.toString());
  });
};

let App: any = function App(props: any) {
  // Устанавливаем глобальный контекст для доступа из funcs.ts
  useEffect(() => {
    setGlobalAppContext({
      $store: props.$store,
      $commitToStore: props.$commitToStore
    });
  }, [props.$store, props.$commitToStore]);

  window.addEventListener('error', function (event) {
    let error = event.error
    if (error) {
      console.log("unhandled_error.message", error.message)
      console.log("unhandled_error.stack", error.stack)

      const isChunkLoadError = (
        error.message?.includes('Loading chunk') ||
        error.message?.includes('Loading CSS chunk') ||
        error.message?.includes('Failed to fetch dynamically imported module') ||
        error.message?.includes('Unexpected token \'<\'') ||
        error.message?.includes('Failed to fetch') ||
        error.message?.includes('NetworkError') ||
        error.message?.includes('ChunkLoadError')
      );

      if (isChunkLoadError) {
        const chunkMatch = error.message.match(/chunk (\d+)/);
        const urlMatch = error.message.match(/\((.*?)\)/);
        const chunkNumber = chunkMatch ? chunkMatch[1] : 'unknown';
        const failedUrl = urlMatch ? urlMatch[1] : 'unknown';

        console.error('[ChunkError] Обнаружена ошибка загрузки чанка:', {
          error: error.message,
          stack: error.stack,
          chunkNumber,
          failedUrl,
          url: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        });

        // Пытаемся очистить только кэш приложения
        if ('caches' in window) {
          console.log('[ChunkError] Очистка кэша приложения...');
          caches.keys().then(cacheNames => {
            cacheNames.forEach(cacheName => {
              if (cacheName.includes('okidoki')) {
                console.log(`[ChunkError] Удаление кэша: ${cacheName}`);
                caches.delete(cacheName);
              }
            });
          });
        }

        // Очищаем кэш webpack
        if ('webpackHotUpdate' in window) {
          console.log('[ChunkError] Обнаружен webpackHotUpdate, перезагрузка страницы...');
          window.location.reload();
          return;
        }

        // Пробуем загрузить чанк повторно
        retryLoadChunk(failedUrl).catch((retryError) => {
          console.error('[ChunkError] Критическая ошибка после всех попыток:', {
            originalError: (error as Error).message,
            retryError: (retryError as Error).message,
            chunkNumber,
            failedUrl,
            url: window.location.href,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString()
          });

          // Если все попытки не удались, используем принудительную перезагрузку
          console.log('[ChunkError] Все попытки исчерпаны, используем принудительную перезагрузку');
          forceReloadWithCacheClear();
        });

        trackGoal("chunk_load_error", {
          error_message: error.message,
          error_stack: error.stack,
          chunk_number: chunkNumber,
          failed_url: failedUrl,
          url: window.location.href,
          user_agent: navigator.userAgent,
          retry_attempt: MAX_RETRY_ATTEMPTS
        });
        
        return;
      }

      StackTrace.fromError(error).then((err) => {
        console.log("StackTrace", err)
        
        trackGoal(
          "unhandled_error",
          {
            unhandled_error_message: error.message,
            unhandled_error_stack: err
          }
        )
      })
    }
  })

  // Добавляем обработчик для ошибок загрузки ресурсов
  window.addEventListener('unhandledrejection', function(event) {
    const error = event.reason;
    
    const isLoadError = (
      error?.message?.includes('Loading') ||
      error?.message?.includes('Failed to fetch') ||
      error?.message?.includes('Network error') ||
      error?.message?.includes('503') ||
      error?.message?.includes('Service Unavailable')
    );

    if (isLoadError) {
      console.error('[ResourceLoadError] Обнаружена ошибка загрузки ресурса:', {
        error: error.message,
        stack: error.stack,
        url: window.location.href,
        resourceUrl: error?.url || 'unknown',
        status: error?.status || 'unknown',
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        connectionType: (navigator as any).connection?.effectiveType || 'unknown',
        performance: {
          resourceCount: performance.getEntriesByType('resource').length,
          totalResourceDuration: performance.getEntriesByType('resource')
            .reduce((total: number, entry: any) => total + entry.duration, 0),
          navigationTiming: performance.timing
        }
      });

      // Отправляем метрику
      trackGoal("resource_load_error", {
        error_message: error.message,
        error_stack: error.stack,
        resource_url: error?.url || 'unknown',
        status: error?.status || 'unknown',
        url: window.location.href,
        user_agent: navigator.userAgent,
        connection_type: (navigator as any).connection?.effectiveType || 'unknown',
        timestamp: new Date().toISOString()
      });
      
      // Агрессивная очистка кэша и принудительная перезагрузка
      console.log('[ResourceLoadError] Выполняем агрессивную очистку кэша...');
      forceReloadWithCacheClear();
      
      event.preventDefault();
    }
  });

  const $router = useRoute()

  const [location, setLocation] = useState(window.location)

  const [shouldBeHeaderHightlighted, setShouldBeHeaderHightlighted] = useState(location.pathname === '/')
  const [shouldBeHeaderFixed, setShouldBeHeaderFixed] = useState(location.pathname === '/')
  const [userStoryPopup, setUserStoryPopup] = useState<any>(null)

  // User redirection
  useEffect(() => {
    // Проверяем параметр cache_clear в URL для принудительной очистки кэша
    const urlParams = new URLSearchParams(window.location.search);
    const shouldClearCache = urlParams.get('cache_clear') === 'true';
    
    if (shouldClearCache) {
      console.log('[App] Обнаружен параметр cache_clear, выполняем очистку кэша...');
      clearAllCaches().then(() => {
        // Удаляем параметр из URL после очистки
        urlParams.delete('cache_clear');
        const newUrl = new URL(window.location.href);
        newUrl.search = urlParams.toString();
        window.history.replaceState({}, '', newUrl.toString());
        console.log('[App] Кэш очищен, параметр удален из URL');
      });
    }

    const isMainOrRegisterPage = ['main', 'register'].includes($router.route.name)
    const allowedWithoutAuthPages = ['contract', 'package', 'legal-help', 'debug']
    // Проверка URL на наличие legal-help
    const isLegalHelpPath = window.location.pathname.includes('legal-help')

    const userHasSession = getToken() != null || isOAuth2User()

    if ($router.route.name == "invite") {
      // Получаем phone_number из URL параметров
      const urlParams = new URLSearchParams(window.location.search);
      const phoneNumber = urlParams.get('phone_number');
      
      props.$commitToStore({
        inviteRoleId: $router.route.params.roleId,
        invitePhoneNumber: phoneNumber
      })
      if (userHasSession) {
        // При переходе по приглашению устанавливаем таб "Команда"
        props.$commitToStore({
          selectedProfileTab: ProfileTabs.Team
        })
        localStorage.setItem('selectedProfileTab', ProfileTabs.Team)
        $router.router.navigate('profile')
      } else {
        $router.router.navigate('register')
      }
      return
    }

    // Если находимся на странице legal-help, не делаем редирект
    if (isLegalHelpPath) {
      return
    }

    // Если находимся на дебажной странице, не делаем редирект
    if ($router.route.name === 'debug') {
      return
    }

    // Не авторизован и не на главной/регистрации
    if (!userHasSession && !isMainOrRegisterPage) {
      if (allowedWithoutAuthPages.includes($router.route.name)) {
        $router.router.navigate(
          $router.route.name,
            {
              contractId: $router.route.params.contractId,
              packageId: $router.route.params.packageId,
              is_first_signer: $router.route.params.is_first_signer,
              additional_signer: $router.route.params.additional_signer,
            }
        )
      } else {
        setTimeout(() => { $router.router.navigate('main') }, 0)
      }
    } else if (userHasSession && isMainOrRegisterPage && !$router.route.params.mcp_client_id) {
      setTimeout(() => { $router.router.navigate('new-contract', { act: 'list' }) }, 0)
    }
  }, [])

  // If authed, set bearer authentication for API requests
  useEffect(() => {
    loadToken()
    if (getToken() || isOAuth2User()) {
      const token = getToken()
      if (token) {
        saveToken(token)
      }
      loadSelf(props)
    }
  }, [])

  useEffect(() => {
    if (props.$store.signedOut) {
      $router.router.navigate('main')
      props.$commitToStore({
        signedOut: false
      })
    }
  }, [props.$store.signedOut])

  useEffect(() => {
    let user_stories = props.$store.userSession?.user_stories
    if (user_stories) {
      let notShownStory = user_stories.filter((story: any) => story.shown == false)[0]
      if (notShownStory) {
        setUserStoryPopup(notShownStory)
      }
    }
  }, [props.$store.userSession?.user_stories])

  // Обновляем информацию о пользователе в ErrorLogger
  useEffect(() => {
    if (props.$store.userSession) {
      errorLogger.setUserSession(props.$store.userSession);
    } else {
      errorLogger.setUserSession(null);
    }
  }, [props.$store.userSession]);

  useEffect(() => {
    setShouldBeHeaderHightlighted($router.route.name === 'main')
    setShouldBeHeaderFixed($router.route.name === 'main')
  }, [$router])

  // Handle notifications
  useEffect(() => {

    if (!props.$store.notification) return

    clearTimeout(notificationTimeout)

    if (props.$store.notification.sticky) {
      return
    }

    notificationTimeout = setTimeout(() => {
      props.$commitToStore({
        notification: null
      })
    }, 5000)
  }, [props.$store.notification])

  const mobile = useMediaQuery(mobileMaxWidthMediaQuery)
  const [helpCenterOpen, setHelpCenterOpen] = useState(false)
  const [supportFabAnchor, setSupportFabAnchor] = useState<null | HTMLElement>(null)
  const [showInitiatorOptions, setShowInitiatorOptions] = useState(false)
  const [signingAssistantDrawerOpen, setSigningAssistantDrawerOpen] = useState(false)
  const [signingAssistantUnread, setSigningAssistantUnread] = useState(0)

  /** Неавторизованный просмотр договора/пакета, предпросмотр брендинга «как клиент» или демо-подписание */
  const isClientPreview = $router.route.params.client_preview === '1'
  const isAsClient = $router.route.params.as_client === '1'
  const legacyUnauthContractFab =
    (!tokenLoaded() || isClientPreview || isAsClient) && ["contract", "package"].includes($router.route.name)

  function shouldOfferInitiator() {
    const isContractPage = ['contract', 'package'].includes($router.route.name)
    const context = (props.$store as any).supportFabContext
    return isContractPage && !!context?.available
  }

  function getInitiatorPhone(): string | null {
    const context = (props.$store as any).supportFabContext
    return context?.initiatorPhone || null
  }

  function toTelHref(phone: string): string {
    const digits = (phone || '').replace(/\D/g, '')
    return `tel:${digits}`
  }

  function getWaPhone(): string | null {
    const phone = getInitiatorPhone()
    if (!phone) return null
    const wa = cleanPhoneNumber(phone)
    return wa.length > 0 ? wa : null
  }

  function getTgPhone(): string | null {
    const wa = getWaPhone()
    if (!wa) return null
    return wa[0] === '+' ? wa : `+${wa}`
  }

  function showMobileFooter(): boolean {
    if (!tokenLoaded() ||
        $router.route.name === 'main' ||
        $router.route.name === 'contract' ||
        $router.route.name === 'package' ||
        $router.route.name === 'debug' ||
        ($router.route.name === 'new-contract' && $router.route.params.act === 'new') ||
        !mobile
    ) {
      return false
    }
    return true
  }

  function showMobileBalance(): boolean {
    if (!tokenLoaded() ||
        $router.route.name === 'main' ||
        $router.route.name === 'contract' ||
        $router.route.name === 'package' ||
        $router.route.name === 'debug' ||
        ($router.route.name === 'new-contract' && $router.route.params.act === 'new') ||
        ($router.route.name === 'packages-v2' || $router.route.name === 'tariffs') || // Скрываем BalanceHolder на странице тарифов
        !mobile
    ) {
      return false
    }
    return true
  }

  function showDesktopFooter() {
    return $router.route.name !== 'new-contract' &&
        $router.route.name !== 'main' &&
        $router.route.name !== 'contract' &&
        $router.route.name !== 'package' &&
        $router.route.name !== 'debug' &&
        !mobile
  }

  function makeDesktopFooter() {
    return <footer>

      {/* Footer left */}

      <div className="__left">
        <img src={imgLogoFull} className="footer-logo" alt="OkiDoki" />

        <div className="social-links">
          <span>Мы в социальных сетях:</span>

          {/*<a href="https://www.instagram.com/try_oki_doki/">*/}
          {/*  <img style={{width: "30px", height: "30px"}} src={InstagramLogo}/>*/}
          {/*</a>*/}

          <a href="https://t.me/okidoki_digital_docs">
            <div style={{display: "flex"}}>
              <Icon icon="telegram-4" className="__telegram" />
            </div>
          </a>

          <a href="https://vk.com/public215560534" >
            <div style={{display: "flex"}}>
              <Icon icon="vk-4" className="__vk" />
            </div>
          </a>
        </div>
      </div>

      {/* Footer right */}

      <div className="__right">
        <div className="copyright-i">
          Copyright &copy; 2021-2025 OkiDoki. Все права защищены
        </div>
      </div>
    </footer>
  }

  function tokenLoaded() {
    return getToken() != null
  }

  useEffect(() => {
    trackPerformance();
  }, []);

  useEffect(() => {
    const utm = captureUtmFirstTouch()
    if (utm) {
      props.$commitToStore({ utm })
    }
  }, []);

  const saCtx = (props.$store as any).signingAssistantContext
  const contractPageBranding = (props.$store as any).contractPageBranding
  const brandingActive = hasActiveBranding(contractPageBranding)
  const brandingRoutes = ['contract', 'package', 'register', 'contract-branding']
  const signingSupportFabRoutes = ['contract', 'package', 'register', 'invite']
  const brandSupportFab =
    brandingActive && signingSupportFabRoutes.includes($router.route.name)
  useApplyContractPageBranding(
    contractPageBranding,
    brandingRoutes.includes($router.route.name) && brandingActive,
    $router.route.name === 'contract-branding'
      ? 'Оформление страницы договора'
      : null
  )
  const headerLogoSrc =
    contractPageBranding?.logo &&
    brandingRoutes.includes($router.route.name)
      ? contractPageBranding.logo
      : (shouldBeHeaderHightlighted ? imgLogoFullHighlighted : imgLogoFull)
  const showBrandingHeaderTitle =
    brandingActive &&
    brandingRoutes.includes($router.route.name) &&
    Boolean(contractPageBranding?.title)
  const showMobileBrandingBar =
    mobile &&
    brandingActive &&
    brandingRoutes.includes($router.route.name) &&
    hasBrandingHeaderContent(contractPageBranding)

  useEffect(() => {
    const root = document.documentElement
    if (showMobileBrandingBar) {
      root.style.setProperty(
        '--contract-mobile-branding-bar-height',
        `${MOBILE_BRANDING_BAR_HEIGHT_PX}px`
      )
      root.classList.add('has-mobile-contract-branding-bar')
      return
    }
    root.style.removeProperty('--contract-mobile-branding-bar-height')
    root.classList.remove('has-mobile-contract-branding-bar')
  }, [showMobileBrandingBar])

  const signingAssistantActive =
    ["register", "invite", "contract"].includes($router.route.name) &&
    !!saCtx?.enabled &&
    !!saCtx?.contractId &&
    !!saCtx?.accessToken

  const onTemplateEditPage =
    $router.route.name === "new-contract" &&
    $router.route.params.act === "new" &&
    !!$router.route.params.templateId

  const templateSupportAiChatOnly =
    onTemplateEditPage &&
    !!(props.$store.userSession?.experiments?.includes(EXPERIMENT_TEMPLATE_SUPPORT_AI_CHAT))

  useEffect(() => {
    if (!signingAssistantActive) {
      setSigningAssistantDrawerOpen(false)
      setSigningAssistantUnread(0)
    }
  }, [signingAssistantActive])

  useEffect(() => {
    if (legacyUnauthContractFab) {
      setHelpCenterOpen(false)
    } else {
      setSupportFabAnchor(null)
      setShowInitiatorOptions(false)
    }
  }, [legacyUnauthContractFab])

  // Render
  return (
      <Suspense fallback={<PageLoader />}>
        <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale={"ru"}
            localeText={ruRU.components.MuiLocalizationProvider.defaultProps.localeText}
        >
          <>

          {/* Header */}

          {$router.route.name !== 'main' && $router.route.name !== 'debug' && <>
            <header className={classnames({
              _fixed: shouldBeHeaderFixed,
              _highlighted: shouldBeHeaderHightlighted
            })}>

              <div className="__left">
                <img src={headerLogoSrc}
                     alt="OkiDoki logo"
                     className="header-logo"
                     onClick={() => {
                        $router.router.navigate(isAuthorized() ? 'profile' : 'main')
                     }}
                />
                {showBrandingHeaderTitle && (
                  <div className="header-brand-title">{contractPageBranding.title}</div>
                )}
              </div>

              <div className="__right">
                {!isAsClient && <Nav />}
              </div>
            </header>
          </>}

          {showMobileBrandingBar && (
            <ContractPageBrandingBar branding={contractPageBranding} />
          )}

          {
            tokenLoaded() && props.$store.showWelcomePopup && !props.$store.inviteRoleId && !isAsClient && !$router.route.params.mcp_client_id &&
            <WelcomePopup
                userSession={props.$store.userSession}
                $commitToStore={props.$commitToStore}
            />
          }

          {/* Pages */}
          <main>
            {$router.route.name === 'main' && <AuthForm />}
            {$router.route.name === 'register' && <RegisterPage />}
            {$router.route.name === 'invite' && <RegisterPage />}

            {tokenLoaded() && $router.route.name === 'another' && <AnotherPage />}
            {tokenLoaded() && $router.route.name === 'main' && !$router.route.params.mcp_client_id && <NewContractPage />}
            {tokenLoaded() && $router.route.name === 'new-contract' && <NewContractPage />}
            {tokenLoaded() && $router.route.name === 'contract-legal-check' && <LegalCheckPage />}
            
            {/* Оборачиваем тяжелые компоненты в Suspense */}
            <React.Suspense fallback={<PageLoader />}>
              {tokenLoaded() && $router.route.name === 'signed-contracts' && <ContractListPage />}
              {$router.route.name === 'contract' && <ContractViewPage />}
              {$router.route.name === 'package' && <ContractViewPage />}
              {tokenLoaded() && $router.route.name === 'new-pdf-contract' && <CreatePDFPage />}
              {tokenLoaded() && $router.route.name === 'contract-statistics' && <ContractStatisticsPage />}
              {tokenLoaded() && $router.route.name === 'legal-help' && <LegalHelpPage />}
            </React.Suspense>

            {tokenLoaded() && $router.route.name === 'profile' && <ProfilePage />}
            {(tokenLoaded() && ($router.route.name === 'tariffs' || $router.route.name === 'packages-v2')) && <TariffsV2Page />}
            {tokenLoaded() && $router.route.name === 'profile.update' && <ProfilePage />}
            {tokenLoaded() && $router.route.name === 'shared-template' && <SharedTemplatePage />}
            {tokenLoaded() && $router.route.name === 'closing-documents' && <ClosingDocumentsPage />}
            {tokenLoaded() && $router.route.name === 'partner-program' && <PartnerProgramPage />}
            {tokenLoaded() && $router.route.name === 'contract-branding' && <ContractBrandingPage />}
            {$router.route.name === 'debug' && <DebugPage />}

          </main>

          {createPortal(
            <React.Fragment>
            {signingAssistantActive && (
                <SigningAssistantChat
                  contractId={saCtx.contractId}
                  accessToken={saCtx.accessToken}
                  open={signingAssistantDrawerOpen}
                  onClose={() => setSigningAssistantDrawerOpen(false)}
                  onUnreadCountChange={setSigningAssistantUnread}
                  formSnapshot={saCtx.formSnapshot ?? null}
                />
              )}

            {$router.route.name !== 'debug' && (
              <Badge
                badgeContent={signingAssistantUnread > 0 ? signingAssistantUnread : 0}
                color="error"
                overlap="circular"
                invisible={!signingAssistantActive || signingAssistantUnread === 0}
                sx={{
                  position: "fixed",
                  bottom: showMobileFooter() ? "93px" : "20px",
                  right: "20px",
                  zIndex: SUPPORT_Z_FAB,
                  "& .MuiBadge-badge": {
                    zIndex: SUPPORT_Z_FAB_UNREAD_BADGE,
                  },
                }}
              >
                <Fab
                  id="support-help-center-fab"
                  className={classnames({
                    "contract-support-fab": brandSupportFab,
                    "onboarding-editor-tour__pulse-fab": props.$store.onboardingEditorTourStep === "help",
                  })}
                  aria-label={
                    signingAssistantActive && signingAssistantUnread > 0
                      ? `Поддержка, непрочитанных в чате помощи: ${signingAssistantUnread}`
                      : legacyUnauthContractFab
                        ? "Поддержка"
                        : "Справочный центр"
                  }
                  sx={{
                    position: "relative",
                    width: 64,
                    height: 64,
                    minHeight: 64,
                    minWidth: 64,
                    background: brandSupportFab
                      ? "var(--contract-brand-primary, #0070FF)"
                      : "#0070FF",
                    "&:hover": {
                      backgroundColor: brandSupportFab
                        ? "var(--contract-brand-primary, #0070FF)"
                        : "#0062E6",
                      filter: brandSupportFab ? "brightness(0.92)" : undefined,
                    },
                  }}
                  onClick={
                    legacyUnauthContractFab
                      ? (e) => setSupportFabAnchor(e.currentTarget)
                      : () => setHelpCenterOpen(true)
                  }
                >
                  <LiveHelpChatIcon sx={{ fontSize: 32, color: "#fff" }} aria-hidden />
                </Fab>
              </Badge>
            )}

            {legacyUnauthContractFab && (
              <Menu
                anchorEl={supportFabAnchor}
                open={Boolean(supportFabAnchor)}
                onClose={() => {
                  setSupportFabAnchor(null)
                  setShowInitiatorOptions(false)
                }}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "bottom", horizontal: "right" }}
                sx={{ zIndex: SUPPORT_Z_MENU }}
                PaperProps={{
                  sx: { zIndex: SUPPORT_Z_MENU },
                }}
              >
                {shouldOfferInitiator() && (
                  <>
                    <MenuItem
                      disabled={!getInitiatorPhone()}
                      onClick={() => setShowInitiatorOptions((prev) => !prev)}
                    >
                      <ListItemIcon>
                        <Person fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Связаться с инициатором"
                        secondary={getInitiatorPhone() ? getInitiatorPhone() : "номер не указан"}
                      />
                    </MenuItem>
                    {showInitiatorOptions && (
                      <>
                        <MenuItem
                          disabled={!getInitiatorPhone()}
                          onClick={() => {
                            const phone = getInitiatorPhone()
                            if (phone) {
                              window.location.href = toTelHref(phone)
                            }
                            setSupportFabAnchor(null)
                            setShowInitiatorOptions(false)
                          }}
                          sx={{ pl: 6 }}
                        >
                          <ListItemIcon>
                            <PhoneIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary="Позвонить" />
                        </MenuItem>
                        <MenuItem
                          disabled={!getWaPhone()}
                          onClick={() => {
                            const wa = getWaPhone()
                            if (wa) {
                              trackGoal("open_wa")
                              openInNewTab(`https://wa.me/${wa}`)
                            }
                            setSupportFabAnchor(null)
                            setShowInitiatorOptions(false)
                          }}
                          sx={{ pl: 6 }}
                        >
                          <ListItemIcon>
                            <WhatsAppIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary="Написать в WhatsApp" />
                        </MenuItem>
                        <MenuItem
                          disabled={!getTgPhone()}
                          onClick={() => {
                            const tg = getTgPhone()
                            if (tg) {
                              trackGoal("open_tg")
                              openInNewTab(`https://t.me/${tg}`)
                            }
                            setSupportFabAnchor(null)
                            setShowInitiatorOptions(false)
                          }}
                          sx={{ pl: 6 }}
                        >
                          <ListItemIcon>
                            <TelegramIcon fontSize="small" sx={{ color: "#229ED9" }} />
                          </ListItemIcon>
                          <ListItemText primary="Написать в Telegram" />
                        </MenuItem>
                        <Divider />
                      </>
                    )}
                    <Divider />
                  </>
                )}
                {signingAssistantActive && (
                  <MenuItem
                    onClick={() => {
                      setSigningAssistantDrawerOpen(true)
                      setSupportFabAnchor(null)
                      setShowInitiatorOptions(false)
                    }}
                  >
                    <ListItemIcon>
                      <ChatIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Техподдержка" secondary="Помощь при подписании" />
                  </MenuItem>
                )}
                {!signingAssistantActive && (
                  <>
                    <MenuItem
                      onClick={() => {
                        openInNewTab(maxSupportLink())
                        setSupportFabAnchor(null)
                        setShowInitiatorOptions(false)
                      }}
                    >
                      <ListItemIcon>
                        <MaxIcon style={{ width: 20, height: 20 }} aria-hidden />
                      </ListItemIcon>
                      <ListItemText primary="Техподдержка в MAX" />
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        openInNewTab(supportLink())
                        setSupportFabAnchor(null)
                        setShowInitiatorOptions(false)
                      }}
                    >
                      <ListItemIcon>
                        <TelegramIcon fontSize="small" sx={{ color: "#229ED9" }} />
                      </ListItemIcon>
                      <ListItemText primary="Техподдержка в Telegram" />
                    </MenuItem>
                  </>
                )}
              </Menu>
            )}

            {!legacyUnauthContractFab && (
              <SupportHelpCenter
                open={helpCenterOpen}
                onClose={() => setHelpCenterOpen(false)}
                canOpenTemplateAiAssistant={
                  $router.route.name === "new-contract" &&
                  $router.route.params.act === "new" &&
                  !!$router.route.params.templateId
                }
                onRequestTemplateAiAssistant={() => {
                  props.$commitToStore({
                    templateAiAssistantOpenRequest: { nonce: Date.now() },
                  })
                  setHelpCenterOpen(false)
                }}
                signingAssistantActive={signingAssistantActive}
                onOpenSigningAssistant={() => setSigningAssistantDrawerOpen(true)}
                shouldOfferInitiator={shouldOfferInitiator()}
                getInitiatorPhone={getInitiatorPhone}
                onCallInitiator={() => {
                  const phone = getInitiatorPhone()
                  if (phone) {
                    window.location.href = toTelHref(phone)
                  }
                }}
                onWhatsAppInitiator={() => {
                  const wa = getWaPhone()
                  if (wa) {
                    trackGoal("open_wa")
                    openInNewTab(`https://wa.me/${wa}`)
                  }
                }}
                onTelegramInitiator={() => {
                  const tg = getTgPhone()
                  if (tg) {
                    trackGoal("open_tg")
                    openInNewTab(`https://t.me/${tg}`)
                  }
                }}
                showOkiDokiMessengers={!signingAssistantActive && !templateSupportAiChatOnly}
                templateAiAssistantButtonLabel={
                  templateSupportAiChatOnly ? "Чат техподдержки" : undefined
                }
                onOpenTelegramSupport={() => openInNewTab(supportLink())}
                onOpenMaxSupport={() => openInNewTab(maxSupportLink())}
              />
            )}
            </React.Fragment>,
            document.body
          )}


          {/* Footer */}

          {
              showDesktopFooter() &&
              makeDesktopFooter()
          }

          {
            showMobileBalance() && <BalanceHolder/>
          }
          {
            showMobileFooter() && <MobileFooter/>
          }

          {createPortal(
            <div
              className={classnames('notification', {
                _hidden: !props.$store.notification
              })}
              style={{ zIndex: UI_LAYER_GLOBAL_NOTIFICATION }}
            >
              {
                  props.$store.notification &&
                  <>
                    <div className="titleContainer">
                      <div></div>
                      <button
                          className="new smallIconed close-button"
                          onClick={() => {
                            props.$commitToStore({
                              notification: null
                            })
                          }}
                      >
                        <Close className="icon" style={{stroke: "#75809E"}} />
                      </button>
                    </div>
                    <div className="commonLabel" style={{marginTop: "-20px", marginRight: "60px"}}>
                      {props.$store.notification.title}
                    </div>
                    {props.$store.notification.content && (
                        <div className="commonSubtitle">
                          {props.$store.notification.content}
                        </div>
                    )}
                  </>
              }
            </div>,
            document.body
          )}


            {
              userStoryPopup &&
                <UserStoryPopup
                    story={userStoryPopup}
                    onClose={() => {
                      setUserStoryPopup(false)
                    }}
                />
            }
            {props.$store.userSession && <OnboardingPayOfferPopup />}
          </>
        </LocalizationProvider>
      </Suspense>
  )
}

App = connect((store) => ({ $store: store }), (dispatch) => ({ $commitToStore: (data: any) => dispatch({ ...data, type: 'S' }) }))(App)

export default App;